// CollapsedMachineMessage — Sprint machine-message-collapse (D1–D6).
//
// A `type='user'` row whose origin is `system` or `wakeup` (the closed
// list in `lib/messages.ts` `isCollapsedMachineOrigin`, D1) is text a
// machine wrote for a machine. Founder directive 2026-09-12: it no longer
// takes a bubble's worth of the conversation — it renders as the same
// one-line marker the compaction divider uses (rule · pill · rule, D2),
// carrying the origin icon + label + the message's day/time stamp, and the
// full text opens on CLICK (D3; not hover — founder-confirmed, same
// reasons as `CompactionDivider` D11 R1 change 7). Founder and `fleet:*`
// rows keep `UserMessage` byte-identical (D1: "por ahora no plegaría los
// mensajes que vienen de otros agentes").
//
// The popover body is the bubble's render, not a re-interpretation (D4):
// `renderTextWithChips` from `UserMessage.tsx`, `whitespace-pre-wrap`, the
// same `AttachmentChip` block, the `[fleet-…]` control frame inside
// `DeveloperOnly` (attribution D6 — `splitFleetFrame` splits on ANY
// non-founder origin, wakeup included, so dropping this block would
// silently eat that line: architect R1 EC-8). No `MarkdownRenderer`: the
// bubble never rendered markdown for user rows and this sprint does not
// change that. Edit / Resend move from under-the-bubble hover icons to a
// footer row inside the popover (D5): nothing a founder could do before is
// lost, it just lives where the text now lives.
//
// Origin → (icon, label) is the same 2-entry map `MessageOriginChip.tsx`
// owns for the bubble chip (`Cog`/"Sistema", `Bell`/"Despertador"). It is
// restated here, not imported, because that component renders a `Pill`
// and the marker's trigger is `DividerPopover`'s button (D2 locks those
// classes); keep the two in lock-step if either ever changes.
//
// Root carries `data-message-id` (D6): user rows render bare in
// `ConversationList`, so this root is the search/rail jump target exactly
// as `UserMessage`'s is. A search hit inside a collapsed row lands on the
// marker with the popover closed — declared cost, edge-case row in
// `docs/edge-cases.md` (sprint spec EC-1), not built here.

import { useState } from 'react';
import { useTranslation } from '../../i18n';
import { Icon } from '../atoms/Icon';
import type { IconName } from '../atoms/Icon';
import type { StoredAttachment } from '../../types';
import { AttachmentChip } from '../AttachmentChip';
import { useDocViewer } from '../../hooks/useDocViewer';
import { formatMessageStamp } from '../../lib/format-message-stamp';
import { DividerPopover } from '../DividerPopover';
import { DeveloperOnly } from '../founder/DeveloperOnly';
import { renderTextWithChips } from './UserMessage';

export type CollapsedMachineOrigin = 'system' | 'wakeup';

export interface CollapsedMachineMessageProps {
  text: string;
  origin: CollapsedMachineOrigin;
  frame?: string | null;
  attachments?: StoredAttachment[] | null;
  agentId?: string;
  messageId?: string;
  timestamp?: string;
  onEdit?: (text: string) => void;
  onResend?: (text: string) => void;
}

const ORIGIN_ICON: Record<CollapsedMachineOrigin, IconName> = {
  system: 'Cog',
  wakeup: 'Bell',
};

const TEST_ID = 'collapsed-machine-message';

export function CollapsedMachineMessage({
  text,
  origin,
  frame,
  attachments,
  agentId,
  messageId,
  timestamp,
  onEdit,
  onResend,
}: CollapsedMachineMessageProps) {
  const { t, locale } = useTranslation();
  const { openDoc } = useDocViewer();
  const [open, setOpen] = useState(false);

  const originLabel = t(origin === 'wakeup' ? 'message.origin.wakeup' : 'message.origin.system');
  const stamp = timestamp
    ? formatMessageStamp(timestamp, locale, {
        today: t('message.stamp.today'),
        yesterday: t('message.stamp.yesterday'),
      })
    : '';
  // One text run (EC-3: no stamp → no separator), same `·` the bubble's
  // stamp line uses between chip and time.
  const label = stamp ? `${originLabel} · ${stamp}` : originLabel;

  return (
    <DividerPopover
      icon={ORIGIN_ICON[origin]}
      label={label}
      title={t('message.collapsed.hint')}
      open={open}
      onOpenChange={setOpen}
      testIdPrefix={TEST_ID}
      rootAttrs={{ 'data-message-id': messageId, 'data-origin': origin }}
    >
      <div className="flex flex-col gap-2">
        <p className="text-caption font-medium text-foreground">{label}</p>
        {frame && (
          <DeveloperOnly>
            <div
              className="text-caption text-muted-foreground/70 whitespace-pre-wrap break-words"
              data-testid={`${TEST_ID}-fleet-frame`}
            >
              {frame}
            </div>
          </DeveloperOnly>
        )}
        {attachments && attachments.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid={`${TEST_ID}-attachments`}>
            {attachments.map((a) => (
              <AttachmentChip
                key={a.id}
                id={a.id}
                route={a.route}
                name={a.displayName}
                size={a.size}
                state="uploaded"
                staticOnBubble
                agentId={agentId}
              />
            ))}
          </div>
        )}
        <div className="max-h-96 overflow-y-auto" data-testid={`${TEST_ID}-scroll`}>
          <div className="text-chat leading-relaxed text-foreground whitespace-pre-wrap break-words">
            {renderTextWithChips(text, openDoc)}
          </div>
        </div>
        {(onEdit || onResend) && (
          <div
            className="flex items-center justify-end gap-0.5"
            data-testid={`${TEST_ID}-actions`}
          >
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(text)}
                title={t('message.edit')}
                aria-label={t('message.edit')}
                data-testid={`${TEST_ID}-edit`}
                className="inline-flex items-center rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-100"
              >
                <Icon name="Pencil" size={14} aria-hidden="true" />
              </button>
            )}
            {onResend && (
              <button
                type="button"
                onClick={() => onResend(text)}
                title={t('message.resend')}
                aria-label={t('message.resend')}
                data-testid={`${TEST_ID}-resend`}
                className="inline-flex items-center rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors duration-100"
              >
                <Icon name="CornerUpRight" size={14} aria-hidden="true" />
              </button>
            )}
          </div>
        )}
      </div>
    </DividerPopover>
  );
}
